/**
 * ApprovalLetterTab Component
 *
 * Displays underwriting approval letter data with PDF generation capability.
 * Fetches data from Encompass via get-encompass-approval edge function.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { TooltipProvider } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { ConditionsPortalService } from '@/services/conditionsPortalService';
import { useToast } from '@/hooks/use-toast';
import {
  Download,
  FileText,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  Clock,
  User,
  Building2,
  DollarSign,
  Percent,
  ListChecks,
  Filter,
  Loader2,
  Link2,
  Copy,
  ChevronDown,
  ExternalLink,
  FileCheck2
} from 'lucide-react';
import { supabase } from '@/utils/supabase';
import {
  ApprovalLetterData,
  formatCurrency,
  formatPercent,
  formatDate
} from '@/types/approval-letter';
import { downloadApprovalLetterPdf } from '@/utils/approval-letter/pdfGenerator';
import { conditionTemplateService } from '@/services/conditionTemplateService';
import type { ConditionTemplateInfo } from '@/types/condition-templates';
import { buildConditionFilename } from '@/utils/conditionFilename';
import { createComponentLogger } from '@/utils/logger';
import type { ProspectLoan } from './types';

const log = createComponentLogger('ApprovalLetterTab', 'encompass-prospects');

interface ApprovalLetterTabProps {
  loan: ProspectLoan;
}

export function ApprovalLetterTab({ loan }: ApprovalLetterTabProps) {
  const { toast } = useToast();
  const [approvalData, setApprovalData] = useState<ApprovalLetterData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [showOpenOnly, setShowOpenOnly] = useState(true); // Default to open only
  const [templateMap, setTemplateMap] = useState<Map<string, ConditionTemplateInfo>>(new Map());
  const [downloadingCondition, setDownloadingCondition] = useState<string | null>(null);

  // Portal URL state - track both LO/Processor and Title/Escrow portals separately
  const [loProcessorPortalUrl, setLoProcessorPortalUrl] = useState<string | null>(null);
  const [titleEscrowPortalUrl, setTitleEscrowPortalUrl] = useState<string | null>(null);

  // Filter conditions based on showOpenOnly toggle
  const filteredConditions = useMemo(() => {
    if (!approvalData) {
return { ptd: [], ptf: [], ac: [] };
}

    const filterFn = (c: ApprovalLetterData['conditions']['ptd'][0]) =>
      showOpenOnly ? c.statusOpen : true;

    return {
      ptd: approvalData.conditions.ptd.filter(filterFn),
      ptf: approvalData.conditions.ptf.filter(filterFn),
      ac: approvalData.conditions.ac.filter(filterFn)
    };
  }, [approvalData, showOpenOnly]);

  // Count open vs total
  const conditionCounts = useMemo(() => {
    if (!approvalData) {
return { ptd: { open: 0, total: 0 }, ptf: { open: 0, total: 0 }, ac: { open: 0, total: 0 } };
}

    return {
      ptd: {
        open: approvalData.conditions.ptd.filter(c => c.statusOpen).length,
        total: approvalData.conditions.ptd.length
      },
      ptf: {
        open: approvalData.conditions.ptf.filter(c => c.statusOpen).length,
        total: approvalData.conditions.ptf.length
      },
      ac: {
        open: approvalData.conditions.ac.filter(c => c.statusOpen).length,
        total: approvalData.conditions.ac.length
      }
    };
  }, [approvalData]);

  const fetchApprovalData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      log.info('Fetching approval data', { loanGuid: loan.encompass_loan_id });

      const response = await supabase.functions.invoke('get-encompass-approval', {
        body: { loanGuid: loan.encompass_loan_id }
      });

      if (response.error) {
        // Include any details from the edge function response
        const errorDetails = response.data?.details || response.error.message;
        throw new Error(errorDetails || 'Failed to fetch approval data');
      }

      log.info('Approval data received', {
        loanNumber: response.data?.loanNumber,
        ptdCount: response.data?.conditions?.ptd?.length,
        ptfCount: response.data?.conditions?.ptf?.length,
        acCount: response.data?.conditions?.ac?.length
      });

      setApprovalData(response.data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch approval data';
      log.error('Error fetching approval data', { error: message });
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [loan.encompass_loan_id]);

  useEffect(() => {
    if (loan.encompass_loan_id) {
      fetchApprovalData();
    }
  }, [fetchApprovalData, loan.encompass_loan_id]);

  // Fetch condition templates on mount
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const templates = await conditionTemplateService.getTemplateInfoMap();
        setTemplateMap(templates);
        log.info('Loaded condition templates', { count: templates.size });
      } catch (err) {
        log.warn('Failed to load condition templates', { error: err });
        // Non-fatal error - templates are optional
      }
    };
    fetchTemplates();
  }, []);

  // Check for existing portal URLs on mount
  // LO/Processor portal uses internal route (no token), Title/Escrow uses token-based access
  useEffect(() => {
    const checkExistingPortalUrls = async () => {
      if (!loan.encompass_loan_id) {
        return;
      }

      // Set internal portal URL for LO/Processor (always available)
      const internalUrl = `${window.location.origin}/internal/conditions/${loan.encompass_loan_id}`;
      setLoProcessorPortalUrl(internalUrl);

      try {
        // Check for existing Title/Escrow token
        const { titleEscrow } = await ConditionsPortalService.getActiveTokensByType(loan.encompass_loan_id);

        if (titleEscrow) {
          const url = ConditionsPortalService.getPortalUrl(titleEscrow.token);
          setTitleEscrowPortalUrl(url);
          log.info('Found existing Title/Escrow portal URL', { loanId: loan.encompass_loan_id });
        }
      } catch (err) {
        log.warn('Failed to check for existing portal URLs', { error: err });
      }
    };
    checkExistingPortalUrls();
  }, [loan.encompass_loan_id]);

  // Copy LO/Processor portal URL
  const handleCopyLoProcessorUrl = async () => {
    if (!loProcessorPortalUrl) {
return;
}

    try {
      await navigator.clipboard.writeText(loProcessorPortalUrl);
      toast({
        title: 'URL copied!',
        description: 'The LO/Processor portal link has been copied to your clipboard.'
      });
    } catch {
      toast({
        title: 'Failed to copy',
        description: 'Please copy the URL manually.',
        variant: 'destructive'
      });
    }
  };

  // Copy Title/Escrow portal URL
  const handleCopyTitleEscrowUrl = async () => {
    if (!titleEscrowPortalUrl) {
return;
}

    try {
      await navigator.clipboard.writeText(titleEscrowPortalUrl);
      toast({
        title: 'URL copied!',
        description: 'The Title/Escrow portal link has been copied to your clipboard.'
      });
    } catch {
      toast({
        title: 'Failed to copy',
        description: 'Please copy the URL manually.',
        variant: 'destructive'
      });
    }
  };

  // Handle condition document download (supports multi-pair via borrowerPairId)
  const handleDownloadConditionDoc = useCallback(async (externalId: string, conditionTitle: string, borrowerPairId?: string) => {
    if (!loan.encompass_loan_id) {
return;
}

    setDownloadingCondition(externalId);
    try {
      log.info('Downloading condition document', { externalId, loanGuid: loan.encompass_loan_id, borrowerPairId });

      // Use template name from templateMap when available, fall back to conditionTitle
      const template = templateMap.get(externalId);
      const templateName = template?.templateName || conditionTitle;

      // Build pair-aware filename using shared helper
      const pair = approvalData?.borrowerPairs?.find(p => p.applicationId === borrowerPairId);
      const filename = buildConditionFilename(templateName, approvalData?.loanNumber || 'loan', pair?.displayLabel);

      await conditionTemplateService.downloadAndSaveConditionDocument(
        externalId,
        loan.encompass_loan_id,
        filename,
        borrowerPairId
      );

      log.info('Condition document downloaded successfully', { externalId, borrowerPairId });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to download document';
      log.error('Error downloading condition document', { externalId, error: message });
      setError(message);
    } finally {
      setDownloadingCondition(null);
    }
  }, [loan.encompass_loan_id, approvalData?.loanNumber, approvalData?.borrowerPairs, templateMap]);

  const handleDownloadPdf = async () => {
    if (!approvalData) {
return;
}

    setDownloading(true);
    try {
      log.info('Generating PDF', { loanNumber: approvalData.loanNumber, templatesCount: templateMap.size });
      await downloadApprovalLetterPdf(approvalData, { templateMap });
      log.info('PDF downloaded successfully');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to generate PDF';
      log.error('Error generating PDF', { error: message });
      setError(message);
    } finally {
      setDownloading(false);
    }
  };

  const handleManageCleanApproval = () => {
    const url = new URL(
      `/internal/conditions/${encodeURIComponent(loan.encompass_loan_id)}`,
      window.location.origin
    );
    url.searchParams.set('cleanApproval', '1');
    window.open(url.toString(), '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return <ApprovalLetterSkeleton />;
  }

  if (error) {
    return (
      <div className="space-y-4">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <Button variant="outline" onClick={fetchApprovalData}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  if (!approvalData) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>No approval data available for this loan.</AlertDescription>
      </Alert>
    );
  }

  const displayedTotal =
    filteredConditions.ptd.length +
    filteredConditions.ptf.length +
    filteredConditions.ac.length;

  const totalOpen = conditionCounts.ptd.open + conditionCounts.ptf.open + conditionCounts.ac.open;
  const totalAll = conditionCounts.ptd.total + conditionCounts.ptf.total + conditionCounts.ac.total;

  return (
    <div className="space-y-6">
      {/* Header with Actions */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Underwriting Conditional Approval</h2>
          <p className="text-sm text-muted-foreground">
            Loan #{approvalData.loanNumber} - {approvalData.borrower.name}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchApprovalData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button disabled={downloading}>
                {downloading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Link2 className="h-4 w-4 mr-2" />
                    Portal / Download
                    <ChevronDown className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {/* Internal Portal URL (always available for staff) */}
              <DropdownMenuItem onClick={handleCopyLoProcessorUrl}>
                <Copy className="h-4 w-4 mr-2" />
                Copy Internal Portal URL
              </DropdownMenuItem>

              {/* Title/Escrow Portal URL - only show if it exists */}
              {titleEscrowPortalUrl && (
                <DropdownMenuItem onClick={handleCopyTitleEscrowUrl}>
                  <Copy className="h-4 w-4 mr-2" />
                  Copy Title/Escrow Portal URL
                </DropdownMenuItem>
              )}

              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleDownloadPdf}>
                <Download className="h-4 w-4 mr-2" />
                Download PDF
              </DropdownMenuItem>

              <DropdownMenuItem onClick={handleManageCleanApproval}>
                <FileCheck2 className="h-4 w-4 mr-2" />
                Manage Clean Approval
              </DropdownMenuItem>

              {/* Open Portal options */}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => window.open(loProcessorPortalUrl!, '_blank')}>
                <ExternalLink className="h-4 w-4 mr-2" />
                Open Internal Portal
              </DropdownMenuItem>
              {titleEscrowPortalUrl && (
                <DropdownMenuItem onClick={() => window.open(titleEscrowPortalUrl, '_blank')}>
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Open Title/Escrow Portal
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Filter Toggle */}
      <div className="flex items-center justify-between bg-muted/50 rounded-lg px-4 py-3">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">
            Showing {displayedTotal} of {totalAll} conditions
            {showOpenOnly && ` (${totalOpen} open)`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            id="show-open-only"
            checked={showOpenOnly}
            onCheckedChange={setShowOpenOnly}
          />
          <Label htmlFor="show-open-only" className="text-sm cursor-pointer">
            Open only
          </Label>
        </div>
      </div>

      {/* Conditions Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <ConditionSummaryCard
          title="Prior to Docs"
          abbreviation="PTD"
          openCount={conditionCounts.ptd.open}
          totalCount={conditionCounts.ptd.total}
          displayCount={filteredConditions.ptd.length}
          color="blue"
        />
        <ConditionSummaryCard
          title="Prior to Funding"
          abbreviation="PTF"
          openCount={conditionCounts.ptf.open}
          totalCount={conditionCounts.ptf.total}
          displayCount={filteredConditions.ptf.length}
          color="amber"
        />
        <ConditionSummaryCard
          title="At Closing"
          abbreviation="AC"
          openCount={conditionCounts.ac.open}
          totalCount={conditionCounts.ac.total}
          displayCount={filteredConditions.ac.length}
          color="emerald"
        />
      </div>

      {/* Loan Summary */}
      <div className="grid grid-cols-2 gap-6">
        {/* Borrower Info */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <User className="h-4 w-4 text-muted-foreground" />
              Borrower Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <InfoRow label="Borrower" value={approvalData.borrower.name} />
            <InfoRow
              label="Credit Score"
              value={approvalData.borrower.creditScore?.toString() || 'N/A'}
            />
            {approvalData.coBorrower && (
              <>
                <InfoRow label="Co-Borrower" value={approvalData.coBorrower.name} />
                <InfoRow
                  label="Credit Score"
                  value={approvalData.coBorrower.creditScore?.toString() || 'N/A'}
                />
              </>
            )}
          </CardContent>
        </Card>

        {/* Property Info */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              Subject Property
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <InfoRow label="Address" value={approvalData.property.address} />
            <InfoRow
              label="Location"
              value={`${approvalData.property.city}, ${approvalData.property.state} ${approvalData.property.zip}`}
            />
            <InfoRow label="Type" value={approvalData.property.propertyType || 'N/A'} />
            <InfoRow label="Units" value={approvalData.property.units?.toString() || '1'} />
          </CardContent>
        </Card>

        {/* Loan Info */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-muted-foreground" />
              Loan Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <InfoRow label="Loan Amount" value={formatCurrency(approvalData.loan.totalLoanAmount)} />
            <InfoRow
              label="Appraised Value"
              value={formatCurrency(approvalData.loan.appraisedValue)}
            />
            <InfoRow
              label="LTV / CLTV"
              value={`${formatPercent(approvalData.loan.ltv)} / ${formatPercent(approvalData.loan.cltv)}`}
            />
            <InfoRow label="Loan Type" value={approvalData.loan.loanType} />
            <InfoRow label="Purpose" value={approvalData.loan.purpose} />
          </CardContent>
        </Card>

        {/* Underwriting Info */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              Underwriting
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <InfoRow
              label="Approved Date"
              value={formatDate(approvalData.underwriting.approvedDate) || 'Pending'}
            />
            <InfoRow
              label="Must Close By"
              value={formatDate(approvalData.underwriting.mustCloseBy) || 'N/A'}
            />
            <InfoRow label="Loan Officer" value={approvalData.underwriting.loanOfficer || 'N/A'} />
            <InfoRow label="Processor" value={approvalData.underwriting.processor || 'N/A'} />
            <InfoRow label="Underwriter" value={approvalData.underwriting.underwriter || 'N/A'} />
          </CardContent>
        </Card>
      </div>

      {/* Rates */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Percent className="h-4 w-4 text-muted-foreground" />
            Rate & Payment Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-4 text-sm">
            <InfoRow label="Note Rate" value={formatPercent(approvalData.lock.noteRate)} />
            <InfoRow label="Qual Rate" value={formatPercent(approvalData.lock.qualRate)} />
            <InfoRow
              label="Lock Expires"
              value={formatDate(approvalData.lock.lockExpires) || 'N/A'}
            />
            <InfoRow
              label="DTI (Front/Back)"
              value={`${formatPercent(approvalData.loan.frontRatio)} / ${formatPercent(approvalData.loan.backRatio)}`}
            />
          </div>
        </CardContent>
      </Card>

      {/* Conditions Preview */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <ListChecks className="h-4 w-4 text-muted-foreground" />
            Conditions Overview ({displayedTotal} shown)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {filteredConditions.ptd.length > 0 && (
            <ConditionsList
              title="Prior to Docs (PTD)"
              conditions={filteredConditions.ptd}
              color="blue"
              templateMap={templateMap}
              onDownload={handleDownloadConditionDoc}
              downloadingCondition={downloadingCondition}
              borrowerPairs={approvalData?.borrowerPairs}
            />
          )}
          {filteredConditions.ptf.length > 0 && (
            <>
              <Separator />
              <ConditionsList
                title="Prior to Funding (PTF)"
                conditions={filteredConditions.ptf}
                color="amber"
                templateMap={templateMap}
                onDownload={handleDownloadConditionDoc}
                downloadingCondition={downloadingCondition}
              />
            </>
          )}
          {filteredConditions.ac.length > 0 && (
            <>
              <Separator />
              <ConditionsList
                title="At Closing (AC)"
                conditions={filteredConditions.ac}
                color="emerald"
                templateMap={templateMap}
                onDownload={handleDownloadConditionDoc}
                downloadingCondition={downloadingCondition}
              />
            </>
          )}
          {displayedTotal === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">
              {showOpenOnly ? 'No open conditions' : 'No conditions'}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Download CTA */}
      <div className="flex justify-center pt-4">
        <Button size="lg" onClick={handleDownloadPdf} disabled={downloading}>
          {downloading ? (
            <>
              <RefreshCw className="h-5 w-5 mr-2 animate-spin" />
              Generating PDF...
            </>
          ) : (
            <>
              <FileText className="h-5 w-5 mr-2" />
              Download Full Approval Letter
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

// Helper Components

interface InfoRowProps {
  label: string;
  value: string;
}

function InfoRow({ label, value }: InfoRowProps) {
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}

interface ConditionSummaryCardProps {
  title: string;
  abbreviation: string;
  openCount: number;
  totalCount: number;
  displayCount: number;
  color: 'blue' | 'amber' | 'emerald';
}

function ConditionSummaryCard({
  title,
  abbreviation,
  openCount,
  totalCount,
  displayCount,
  color
}: ConditionSummaryCardProps) {
  const colorClasses = {
    blue: 'bg-blue-50 border-blue-200 dark:bg-blue-950 dark:border-blue-800',
    amber: 'bg-amber-50 border-amber-200 dark:bg-amber-950 dark:border-amber-800',
    emerald: 'bg-emerald-50 border-emerald-200 dark:bg-emerald-950 dark:border-emerald-800'
  };

  const textClasses = {
    blue: 'text-blue-600 dark:text-blue-400',
    amber: 'text-amber-600 dark:text-amber-400',
    emerald: 'text-emerald-600 dark:text-emerald-400'
  };

  const clearedCount = totalCount - openCount;

  return (
    <div className={`rounded-lg border p-4 ${colorClasses[color]}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className={`text-2xl font-bold ${textClasses[color]}`}>{displayCount}</p>
          <p className="text-xs text-muted-foreground mt-1">
            {openCount} open / {clearedCount} cleared
          </p>
        </div>
        <Badge variant="outline" className={textClasses[color]}>
          {abbreviation}
        </Badge>
      </div>
    </div>
  );
}

interface ConditionsListProps {
  title: string;
  conditions: ApprovalLetterData['conditions']['ptd'];
  color: 'blue' | 'amber' | 'emerald';
  templateMap: Map<string, ConditionTemplateInfo>;
  onDownload: (externalId: string, conditionTitle: string, borrowerPairId?: string) => void;
  downloadingCondition: string | null;
  borrowerPairs?: ApprovalLetterData['borrowerPairs'];
}

function ConditionsList({
  title,
  conditions,
  color,
  templateMap,
  onDownload,
  downloadingCondition,
  borrowerPairs
}: ConditionsListProps) {
  const hasMultiplePairs = borrowerPairs && borrowerPairs.length > 1;
  const dotClasses = {
    blue: 'bg-blue-500',
    amber: 'bg-amber-500',
    emerald: 'bg-emerald-500'
  };

  // Status badge styling
  const getStatusBadge = (condition: ApprovalLetterData['conditions']['ptd'][0]) => {
    if (condition.statusOpen) {
      return (
        <Badge variant="outline" className="text-xs bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-950 dark:text-yellow-300 dark:border-yellow-800">
          Open
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-300 dark:border-green-800">
        <CheckCircle2 className="h-3 w-3 mr-1" />
        {condition.status}
      </Badge>
    );
  };

  return (
    <TooltipProvider>
      <div className="space-y-2">
        <h4 className="text-sm font-medium">{title} ({conditions.length})</h4>
        <ul className="space-y-2">
          {conditions.map((condition) => {
            const template = templateMap.get(condition.externalId);
            const hasTemplate = !!template;
            const isDownloading = downloadingCondition === condition.externalId;

            return (
              <li key={condition.id} className="flex items-start gap-2 text-sm">
                <span className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${dotClasses[color]}`} />
                <div className="flex-1 min-w-0">
                  {hasTemplate && hasMultiplePairs && borrowerPairs ? (
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-foreground">{condition.title}</span>
                        {getStatusBadge(condition)}
                      </div>
                      {condition.externalDescription && (
                        <p className="text-muted-foreground text-xs mt-0.5">
                          {condition.externalDescription}
                        </p>
                      )}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            disabled={isDownloading}
                            className="mt-1 inline-flex items-center gap-1 text-xs text-blue-700 hover:text-blue-800 hover:underline disabled:opacity-50 disabled:cursor-wait"
                          >
                            {isDownloading ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <Download className="h-3 w-3" />
                            )}
                            Choose borrower pair
                            <ChevronDown className="h-3 w-3" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                          {borrowerPairs.map((pair) => (
                            <DropdownMenuItem
                              key={pair.applicationId || pair.index}
                              onClick={() => onDownload(condition.externalId, condition.title, pair.applicationId)}
                            >
                              {pair.displayLabel}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  ) : hasTemplate ? (
                    <button
                      onClick={() => onDownload(condition.externalId, condition.title)}
                      disabled={isDownloading}
                      className="text-blue-700 hover:text-blue-800 hover:underline cursor-pointer disabled:opacity-50 disabled:cursor-wait text-left block"
                    >
                      <div className="flex items-center gap-2 flex-wrap">
                        {isDownloading ? (
                          <span className="flex items-center gap-1.5 font-medium">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            {condition.title}
                          </span>
                        ) : (
                          <span className="font-medium">{condition.title}</span>
                        )}
                        {getStatusBadge(condition)}
                      </div>
                      {condition.externalDescription && (
                        <p className="text-xs mt-0.5">
                          {condition.externalDescription}
                        </p>
                      )}
                    </button>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-foreground">{condition.title}</span>
                        {getStatusBadge(condition)}
                      </div>
                      {condition.externalDescription && (
                        <p className="text-muted-foreground text-xs mt-0.5">
                          {condition.externalDescription}
                        </p>
                      )}
                    </>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </TooltipProvider>
  );
}

function ApprovalLetterSkeleton() {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <Skeleton className="h-6 w-64" />
          <Skeleton className="h-4 w-48" />
        </div>
        <Skeleton className="h-10 w-32" />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Skeleton className="h-24 rounded-lg" />
        <Skeleton className="h-24 rounded-lg" />
        <Skeleton className="h-24 rounded-lg" />
      </div>

      <div className="grid grid-cols-2 gap-6">
        <Skeleton className="h-40 rounded-lg" />
        <Skeleton className="h-40 rounded-lg" />
        <Skeleton className="h-40 rounded-lg" />
        <Skeleton className="h-40 rounded-lg" />
      </div>

      <Skeleton className="h-64 rounded-lg" />
    </div>
  );
}
